import { AsyncAutocompleteSource } from '@components/v2/form/AsyncAutocomplete/asyncAutocomplete.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCustomerUnauthorizedMessages } from '../../../../hooks/useCustomerUnauthorizedMessages';
import useHandleApiError from '../../../../hooks/useHandleApiError';
import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { WithId } from '../../../../types/Base.types';
import { retrieveOrderProductFromUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { getStatusToEditByRole } from '../../actions/utils/orderSummaryAction.utils';
import { useOrderProductsContext } from '../../contexts/OrderProducts.context';
import { CopyToExistingOrderDTO } from '../../products/dtos/copy-to-existing-order.dto';
import { BatchOrderProduct } from '../../products/interfaces/order-products-repository.types';
import { orderProductsRepository } from '../../products/orderProducts.repository';
import CopyOrderProductsBaseModal from '../components/CopyOrderProductsBaseModal/CopyOrderProductsBaseModal';
import {
  CopyOrderProductsToExistingOrderFormFields,
  CopyOrderProductsToExistingOrderModalProps,
} from './CopyOrderProductsToExistingOrderModal.interfaces';
import CopyOrderProductsToExistingOrderForm from './components/CopyOrderProductsToExistingOrderForm/CopyOrderProductsToExistingOrderForm';
import { buildCopyOrderProductsToExistingOrderSchema } from './copyOrderProductsToExistingOrderModal.schema';
import { CopyModeOnItemsConflictEnum } from './enums/CopyModeOnItemsConflict.enum';

const CopyOrderProductsToExistingOrderModal = ({
  isOpen,
  selectedItems,
  isAllItemsSelected,
  onClose,
}: CopyOrderProductsToExistingOrderModalProps) => {
  const { id: orderId } = useParams<WithId>();
  const { translate } = useTranslation();
  const { userRole } = useApplicationContext();
  const { handleMessage } = snackbarStore;
  const { getApiErrorMessage } = useHandleApiError();
  const { showUnauthorizedOrderProducts } = useCustomerUnauthorizedMessages();
  const { onRemoveProducts } = useOrderProductsContext();
  const repository = orderProductsRepository(orderId);

  const source: AsyncAutocompleteSource = {
    url: '/orders/v2',
    order: { createdAt: 'DESC' },
    queryField: 'codeOrCustomer',
    limit: 20,
    defaultQuery: {
      status: getStatusToEditByRole(userRole),
    },
  };

  const defaultValues: CopyOrderProductsToExistingOrderFormFields = {
    orders: [],
    copyModeOnItemsConflict: CopyModeOnItemsConflictEnum.REPLACE_ORIGINAL,
    removeFromOrigin: true,
  };

  const copyOrderProductsForm = useForm<CopyOrderProductsToExistingOrderFormFields>({
    defaultValues,
    resolver: zodResolver(buildCopyOrderProductsToExistingOrderSchema(translate)),
  });

  const handleSubmit = async (values: CopyOrderProductsToExistingOrderFormFields) => {
    const [order] = values.orders;
    if (!order) {
      return;
    }

    const orderProducts: BatchOrderProduct[] = Array.from(selectedItems)
      .map((item) => retrieveOrderProductFromUniqueIdentifier(item))
      .filter((item) => item !== null);
    const data: CopyToExistingOrderDTO = {
      filter: {
        orderProducts: isAllItemsSelected ? undefined : orderProducts,
        allOrderProducts: isAllItemsSelected,
      },
      options: {
        removeFromOrigin: values.removeFromOrigin,
        copyModeOnItemsConflict: values.copyModeOnItemsConflict,
      },
      to: {
        orderId: order.id,
      },
    };
    const response = await repository.copyToExistingOrder(data);
    if (!response.ok) {
      const { code } = response?.data || {};

      if (code === LangEnum.API_EXCEPTION_CUSTOMER_UNAUTHORIZED_TO_COPY_ORDER_PRODUCTS) {
        showUnauthorizedOrderProducts();
        return;
      }

      handleMessage({
        type: 'error',
        message: getApiErrorMessage(
          response,
          translate(LangEnum.COPY_TO_EXISTING_ORDER_ERROR_MESSAGE, { order: order.code }),
        ),
      });

      copyOrderProductsForm.reset(values);
    } else {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.COPY_TO_EXISTING_ORDER_SUCCESS_MESSAGE, { order: order.code }),
      });

      if (values.removeFromOrigin) {
        await onRemoveProducts(orderProducts);
      }
      onClose(values.removeFromOrigin);
    }
  };

  return (
    <CopyOrderProductsBaseModal<CopyOrderProductsToExistingOrderFormFields>
      isOpen={isOpen}
      form={copyOrderProductsForm}
      title={LangEnum.COPY_ORDER_PRODUCTS_TO_EXISTING_ORDER}
      question={LangEnum.COPY_ORDER_PRODUCTS_TO_EXISTING_ORDER_QUESTION}
      description={LangEnum.COPY_ORDER_PRODUCTS_TO_EXISTING_ORDER_DESCRIPTION}
      copyButtonLabel={LangEnum.GLOBAL_COPY}
      handleSubmit={handleSubmit}
      onClose={onClose}
    >
      <CopyOrderProductsToExistingOrderForm
        source={source}
        form={copyOrderProductsForm}
        handleSubmit={handleSubmit}
      />
    </CopyOrderProductsBaseModal>
  );
};

export default CopyOrderProductsToExistingOrderModal;
